/**
 * Date utility functions for consistent timezone handling
 * All dates stored in backend are UTC, but displayed in user's local timezone
 */

/**
 * Format a date string to user's local date
 * @param {string} dateString - ISO date string from backend
 * @returns {string} - Formatted local date string
 */
export const formatLocalDate = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

/**
 * Format a date string to user's local date and time
 * @param {string} dateString - ISO date string from backend
 * @returns {string} - Formatted local date and time string
 */
export const formatLocalDateTime = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  });
};

/**
 * Format a date string to user's local time only
 * @param {string} dateString - ISO date string from backend
 * @returns {string} - Formatted local time string
 */
export const formatLocalTime = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleTimeString(undefined, {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  });
};

/**
 * Convert local date input (YYYY-MM-DD) to ISO string for backend
 * Treats the input as local date and converts to UTC
 * @param {string} dateString - Date string from input (YYYY-MM-DD)
 * @returns {string} - ISO string in UTC
 */
export const localDateToUTC = (dateString) => {
  if (!dateString) return null;
  const date = new Date(dateString + 'T00:00:00');
  return date.toISOString();
};

/**
 * Convert local datetime input to ISO string for backend
 * @param {string} dateString - Date string (YYYY-MM-DD)
 * @param {string} timeString - Time string (HH:MM)
 * @returns {string} - ISO string in UTC
 */
export const localDateTimeToUTC = (dateString, timeString = '00:00') => {
  if (!dateString) return null;
  const date = new Date(`${dateString}T${timeString}:00`);
  return date.toISOString();
};

/**
 * Convert UTC ISO string to local date for input field (YYYY-MM-DD)
 * @param {string} isoString - ISO date string from backend
 * @returns {string} - Date string for input field
 */
export const utcToLocalDateInput = (isoString) => {
  if (!isoString) return '';
  const date = new Date(isoString);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * Convert UTC ISO string to local time for input field (HH:MM)
 * @param {string} isoString - ISO date string from backend
 * @returns {string} - Time string for input field
 */
export const utcToLocalTimeInput = (isoString) => {
  if (!isoString) return '';
  const date = new Date(isoString);
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${hours}:${minutes}`;
};

/**
 * Get today's date in local timezone for input field
 * @returns {string} - Today's date (YYYY-MM-DD)
 */
export const getTodayLocal = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * Check if a date is in the past (compared to user's local time)
 * @param {string} dateString - ISO date string from backend
 * @returns {boolean} - True if date is in the past
 */
export const isPast = (dateString) => {
  if (!dateString) return false;
  const date = new Date(dateString);
  const now = new Date();
  return date < now;
};

/**
 * Check if a date is today (in user's local timezone)
 * @param {string} dateString - ISO date string from backend
 * @returns {boolean} - True if date is today
 */
export const isToday = (dateString) => {
  if (!dateString) return false;
  const date = new Date(dateString);
  const now = new Date();
  return date.getDate() === now.getDate() &&
    date.getMonth() === now.getMonth() &&
    date.getFullYear() === now.getFullYear();
};

/**
 * Get relative time description (e.g., "2 days ago", "in 3 hours")
 * @param {string} dateString - ISO date string from backend
 * @returns {string} - Relative time description
 */
export const getRelativeTime = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = date - now;
  const diffSec = Math.abs(diffMs / 1000);
  const diffMin = diffSec / 60;
  const diffHour = diffMin / 60;
  const diffDay = diffHour / 24;

  if (diffSec < 60) {
    return 'just now';
  } else if (diffMin < 60) {
    const mins = Math.floor(diffMin);
    return diffMs < 0 ? `${mins} min ago` : `in ${mins} min`;
  } else if (diffHour < 24) {
    const hours = Math.floor(diffHour);
    return diffMs < 0 ? `${hours}h ago` : `in ${hours}h`;
  } else if (diffDay < 7) {
    const days = Math.floor(diffDay);
    return diffMs < 0 ? `${days}d ago` : `in ${days}d`;
  } else {
    return formatLocalDate(dateString);
  }
};
